import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  createIndustrialCostSecondaryAllocation,
  deleteIndustrialCostSecondaryAllocation,
  getIndustrialCostEconomicMovements,
  getIndustrialCostMovementClassifications,
  getIndustrialCostPrimaryAllocations,
  getIndustrialCostSecondaryAllocations,
  getIndustrialCostStages,
} from '../services/industrialCostService';
import IndustrialCostSectionPlaceholder from '../components/IndustrialCostSectionPlaceholder';
import type {
  CreateSecondaryAllocationPayload,
  EconomicMovement,
  MovementClassification,
  SecondaryAllocation,
  Stage,
} from '../types/industrialCost';

import { esNumberFormat } from '../utils/numberFormat';
function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const maybeResponse = error as {
      response?: { data?: { detail?: string } };
      message?: string;
    };

    if (maybeResponse.response?.data?.detail) {
      return maybeResponse.response.data.detail;
    }

    if (maybeResponse.message) {
      return maybeResponse.message;
    }
  }

  return 'Se produjo un error inesperado.';
}

function formatAmount(value: number): string {
  return esNumberFormat({
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatPct(value: number): string {
  return esNumberFormat({
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

function getCompletionStatus(totalPct: number): {
  label: string;
  tone: 'success' | 'warning' | 'error' | 'default';
} {
  if (Math.abs(totalPct - 100) < 0.0001) {
    return { label: 'Completo', tone: 'success' };
  }

  if (totalPct === 0) {
    return { label: 'Sin reparto', tone: 'default' };
  }

  if (totalPct > 100) {
    return { label: 'Excedido', tone: 'error' };
  }

  return { label: 'Incompleto', tone: 'warning' };
}

function getStatusChipStyles(
  tone: 'success' | 'warning' | 'error' | 'default',
): Record<string, unknown> {
  if (tone === 'success') {
    return {
      bgcolor: 'rgba(34,197,94,0.14)',
      color: '#86efac',
      border: '1px solid rgba(34,197,94,0.28)',
    };
  }

  if (tone === 'warning') {
    return {
      bgcolor: 'rgba(245,158,11,0.14)',
      color: '#fcd34d',
      border: '1px solid rgba(245,158,11,0.28)',
    };
  }

  if (tone === 'error') {
    return {
      bgcolor: 'rgba(239,68,68,0.14)',
      color: '#fca5a5',
      border: '1px solid rgba(239,68,68,0.28)',
    };
  }

  return {
    bgcolor: 'rgba(148,163,184,0.12)',
    color: '#cbd5e1',
    border: '1px solid rgba(148,163,184,0.2)',
  };
}

function getAuxiliaryAccent(index: number): {
  borderColor: string;
  softBg: string;
  amountColor: string;
  badgeBg: string;
  badgeColor: string;
} {
  const palette = [
    {
      borderColor: 'rgba(168,85,247,0.45)',
      softBg: 'linear-gradient(180deg, rgba(168,85,247,0.12) 0%, rgba(15,23,42,0.72) 100%)',
      amountColor: '#d8b4fe',
      badgeBg: 'rgba(168,85,247,0.14)',
      badgeColor: '#e9d5ff',
    },
    {
      borderColor: 'rgba(236,72,153,0.45)',
      softBg: 'linear-gradient(180deg, rgba(236,72,153,0.12) 0%, rgba(15,23,42,0.72) 100%)',
      amountColor: '#f9a8d4',
      badgeBg: 'rgba(236,72,153,0.14)',
      badgeColor: '#fbcfe8',
    },
    {
      borderColor: 'rgba(217,70,239,0.45)',
      softBg: 'linear-gradient(180deg, rgba(217,70,239,0.12) 0%, rgba(15,23,42,0.72) 100%)',
      amountColor: '#f0abfc',
      badgeBg: 'rgba(217,70,239,0.14)',
      badgeColor: '#f5d0fe',
    },
  ];

  return palette[index % palette.length];
}

function getPrimaryAccent(index: number): {
  borderColor: string;
  softBg: string;
  amountColor: string;
  badgeBg: string;
  badgeColor: string;
} {
  const palette = [
    {
      borderColor: 'rgba(56,189,248,0.45)',
      softBg: 'linear-gradient(180deg, rgba(56,189,248,0.12) 0%, rgba(15,23,42,0.72) 100%)',
      amountColor: '#7dd3fc',
      badgeBg: 'rgba(56,189,248,0.14)',
      badgeColor: '#bae6fd',
    },
    {
      borderColor: 'rgba(59,130,246,0.45)',
      softBg: 'linear-gradient(180deg, rgba(59,130,246,0.12) 0%, rgba(15,23,42,0.72) 100%)',
      amountColor: '#93c5fd',
      badgeBg: 'rgba(59,130,246,0.14)',
      badgeColor: '#bfdbfe',
    },
    {
      borderColor: 'rgba(99,102,241,0.45)',
      softBg: 'linear-gradient(180deg, rgba(99,102,241,0.12) 0%, rgba(15,23,42,0.72) 100%)',
      amountColor: '#a5b4fc',
      badgeBg: 'rgba(99,102,241,0.14)',
      badgeColor: '#c7d2fe',
    },
  ];

  return palette[index % palette.length];
}

type SecondaryStatusFilter = 'all' | 'no-allocation' | 'incomplete' | 'complete' | 'exceeded';

type StageDraftRow = {
  target_stage_id: string;
  target_stage_name: string;
  allocation_pct: string;
};

type SummaryCardProps = {
  label: string;
  value: string;
  helper: string;
};

function SummaryCard({ label, value, helper }: SummaryCardProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid rgba(148,163,184,0.18)',
        bgcolor: 'rgba(15,23,42,0.5)',
        px: 2.25,
        py: 2,
      }}
    >
      <Stack spacing={0.65}>
        <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
          {label}
        </Typography>
        <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 900 }}>
          {value}
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.5 }}>
          {helper}
        </Typography>
      </Stack>
    </Paper>
  );
}

export default function IndustrialCostSecondaryAllocationsView() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedAuxiliaryStage, setSelectedAuxiliaryStage] = useState<Stage | null>(null);
  const [editingRows, setEditingRows] = useState<StageDraftRow[]>([]);
  const [tableSearch, setTableSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<SecondaryStatusFilter>('all');

  const [
    stagesQuery,
    movementsQuery,
    classificationsQuery,
    primaryAllocationsQuery,
    secondaryAllocationsQuery,
  ] = useQueries({
    queries: [
      {
        queryKey: ['industrial-cost-stages'],
        queryFn: getIndustrialCostStages,
      },
      {
        queryKey: ['industrial-cost-economic-movements'],
        queryFn: getIndustrialCostEconomicMovements,
      },
      {
        queryKey: ['industrial-cost-movement-classifications'],
        queryFn: getIndustrialCostMovementClassifications,
      },
      {
        queryKey: ['industrial-cost-primary-allocations'],
        queryFn: getIndustrialCostPrimaryAllocations,
      },
      {
        queryKey: ['industrial-cost-secondary-allocations'],
        queryFn: getIndustrialCostSecondaryAllocations,
      },
    ],
  });

  const stages = stagesQuery.data ?? [];
  const movements = movementsQuery.data ?? [];
  const classifications = classificationsQuery.data ?? [];
  const primaryAllocations = primaryAllocationsQuery.data ?? [];
  const secondaryAllocations = secondaryAllocationsQuery.data ?? [];

  const stagesMap = useMemo(
    () => new Map<string, Stage>(stages.map((item) => [item.id, item])),
    [stages],
  );

  const classificationsMap = useMemo(
    () => new Map<string, MovementClassification>(classifications.map((item) => [item.movement_id, item])),
    [classifications],
  );

  const activeStages = useMemo(
    () =>
      [...stages]
        .filter((stage) => stage.is_active)
        .sort((a, b) => a.display_order - b.display_order),
    [stages],
  );

  const auxiliaryStages = useMemo(
    () => activeStages.filter((stage) => stage.stage_type === 'auxiliary'),
    [activeStages],
  );

  const primaryStages = useMemo(
    () => activeStages.filter((stage) => stage.stage_type === 'primary'),
    [activeStages],
  );

  const eligibleIndirectMovements = useMemo(() => {
    return movements.filter((movement: EconomicMovement) => {
      const classification = classificationsMap.get(movement.id);

      return (
        classification?.classification_status === 'classified' &&
        classification.economic_type === 'cost' &&
        classification.cost_type === 'indirect'
      );
    });
  }, [movements, classificationsMap]);

  const stageBaseCostFromPrimary = useMemo(() => {
    const totals = new Map<string, number>();

    activeStages.forEach((stage) => {
      totals.set(stage.id, 0);
    });

    primaryAllocations.forEach((allocation) => {
      const movement = eligibleIndirectMovements.find((item) => item.id === allocation.movement_id);
      if (!movement) {
        return;
      }

      const current = totals.get(allocation.stage_id) ?? 0;
      const assignedAmount = movement.amount * (allocation.allocation_pct / 100);
      totals.set(allocation.stage_id, current + assignedAmount);
    });

    return totals;
  }, [activeStages, primaryAllocations, eligibleIndirectMovements]);

  const secondaryAllocationsBySourceStage = useMemo(() => {
    const map = new Map<string, SecondaryAllocation[]>();

    secondaryAllocations.forEach((allocation) => {
      const current = map.get(allocation.source_stage_id) ?? [];
      current.push(allocation);
      map.set(allocation.source_stage_id, current);
    });

    return map;
  }, [secondaryAllocations]);

  const secondaryTotalsBySourceStage = useMemo(() => {
    const totals = new Map<string, number>();

    secondaryAllocationsBySourceStage.forEach((allocationsForStage, sourceStageId) => {
      const total = allocationsForStage.reduce((acc, allocation) => acc + allocation.allocation_pct, 0);
      totals.set(sourceStageId, total);
    });

    return totals;
  }, [secondaryAllocationsBySourceStage]);

  const redistributedAmountByAuxiliaryStage = useMemo(() => {
    const totals = new Map<string, number>();

    auxiliaryStages.forEach((stage) => {
      const baseCost = stageBaseCostFromPrimary.get(stage.id) ?? 0;
      const pct = secondaryTotalsBySourceStage.get(stage.id) ?? 0;
      totals.set(stage.id, baseCost * (pct / 100));
    });

    return totals;
  }, [auxiliaryStages, stageBaseCostFromPrimary, secondaryTotalsBySourceStage]);

  const pendingAmountByAuxiliaryStage = useMemo(() => {
    const totals = new Map<string, number>();

    auxiliaryStages.forEach((stage) => {
      const baseCost = stageBaseCostFromPrimary.get(stage.id) ?? 0;
      const redistributed = redistributedAmountByAuxiliaryStage.get(stage.id) ?? 0;
      totals.set(stage.id, Math.max(baseCost - redistributed, 0));
    });

    return totals;
  }, [auxiliaryStages, stageBaseCostFromPrimary, redistributedAmountByAuxiliaryStage]);

  const receivedFromAuxiliariesByPrimaryStage = useMemo(() => {
    const totals = new Map<string, number>();

    primaryStages.forEach((stage) => {
      totals.set(stage.id, 0);
    });

    secondaryAllocations.forEach((allocation) => {
      const auxiliaryBaseCost = stageBaseCostFromPrimary.get(allocation.source_stage_id) ?? 0;
      const receivedAmount = auxiliaryBaseCost * (allocation.allocation_pct / 100);
      const current = totals.get(allocation.target_stage_id) ?? 0;
      totals.set(allocation.target_stage_id, current + receivedAmount);
    });

    return totals;
  }, [primaryStages, secondaryAllocations, stageBaseCostFromPrimary]);

  const finalAccumulatedCostByPrimaryStage = useMemo(() => {
    const totals = new Map<string, number>();

    primaryStages.forEach((stage) => {
      const ownBase = stageBaseCostFromPrimary.get(stage.id) ?? 0;
      const received = receivedFromAuxiliariesByPrimaryStage.get(stage.id) ?? 0;
      totals.set(stage.id, ownBase + received);
    });

    return totals;
  }, [primaryStages, stageBaseCostFromPrimary, receivedFromAuxiliariesByPrimaryStage]);

  const totalAuxiliaryBaseCost = useMemo(
    () => auxiliaryStages.reduce((acc, stage) => acc + (stageBaseCostFromPrimary.get(stage.id) ?? 0), 0),
    [auxiliaryStages, stageBaseCostFromPrimary],
  );

  const summary = useMemo(() => {
    let complete = 0;
    let incomplete = 0;
    let noAllocation = 0;

    auxiliaryStages.forEach((stage) => {
      const totalPct = secondaryTotalsBySourceStage.get(stage.id) ?? 0;

      if (Math.abs(totalPct - 100) < 0.0001) {
        complete += 1;
      } else if (totalPct === 0) {
        noAllocation += 1;
      } else {
        incomplete += 1;
      }
    });

    return {
      auxiliaryCount: auxiliaryStages.length,
      primaryCount: primaryStages.length,
      totalAuxiliaryBaseCost,
      complete,
      incomplete,
      noAllocation,
    };
  }, [auxiliaryStages, primaryStages, totalAuxiliaryBaseCost, secondaryTotalsBySourceStage]);

  const filteredAuxiliaryStages = useMemo(() => {
    const normalizedSearch = tableSearch.trim().toLowerCase();

    return auxiliaryStages.filter((stage) => {
      const stageAllocations = secondaryAllocationsBySourceStage.get(stage.id) ?? [];
      const totalPct = secondaryTotalsBySourceStage.get(stage.id) ?? 0;
      const redistributedAmount = redistributedAmountByAuxiliaryStage.get(stage.id) ?? 0;
      const pendingAmount = pendingAmountByAuxiliaryStage.get(stage.id) ?? 0;
      const status = getCompletionStatus(totalPct);

      const matchesStatus =
        statusFilter === 'all'
          ? true
          : statusFilter === 'no-allocation'
            ? totalPct === 0
            : statusFilter === 'incomplete'
              ? totalPct > 0 && totalPct < 100
              : statusFilter === 'complete'
                ? Math.abs(totalPct - 100) < 0.0001
                : totalPct > 100;

      if (!matchesStatus) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const targetsText = stageAllocations
        .map((allocation) => {
          const target = stagesMap.get(allocation.target_stage_id);
          return `${target?.name || ''} ${target?.code || ''} ${formatPct(allocation.allocation_pct)}%`;
        })
        .join(' ');

      const haystack = [
        stage.name,
        stage.code,
        formatAmount(stageBaseCostFromPrimary.get(stage.id) ?? 0),
        formatPct(totalPct),
        formatAmount(redistributedAmount),
        formatAmount(pendingAmount),
        status.label,
        targetsText,
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [
    auxiliaryStages,
    tableSearch,
    statusFilter,
    secondaryAllocationsBySourceStage,
    secondaryTotalsBySourceStage,
    redistributedAmountByAuxiliaryStage,
    pendingAmountByAuxiliaryStage,
    stagesMap,
    stageBaseCostFromPrimary,
  ]);

  const openEditor = (stage: Stage) => {
    const stageAllocations = secondaryAllocationsBySourceStage.get(stage.id) ?? [];
    const totalsByTarget = new Map<string, number>();

    stageAllocations.forEach((allocation) => {
      const current = totalsByTarget.get(allocation.target_stage_id) ?? 0;
      totalsByTarget.set(allocation.target_stage_id, current + allocation.allocation_pct);
    });

    setEditingRows(
      primaryStages.map((primaryStage) => ({
        target_stage_id: primaryStage.id,
        target_stage_name: primaryStage.name,
        allocation_pct: totalsByTarget.has(primaryStage.id)
          ? String(totalsByTarget.get(primaryStage.id) ?? 0)
          : '',
      })),
    );
    setSelectedAuxiliaryStage(stage);
  };

  const closeEditor = () => {
    setSelectedAuxiliaryStage(null);
    setEditingRows([]);
  };

  const handleRowPctChange = (targetStageId: string, value: string) => {
    setEditingRows((prev) =>
      prev.map((row) =>
        row.target_stage_id === targetStageId ? { ...row, allocation_pct: value } : row,
      ),
    );
  };

  const editorTotals = useMemo(() => {
    const totalPct = editingRows.reduce((acc, row) => {
      const parsed = Number(row.allocation_pct);
      if (row.allocation_pct.trim() === '' || Number.isNaN(parsed)) {
        return acc;
      }
      return acc + parsed;
    }, 0);

    const hasInvalidValue = editingRows.some((row) => {
      if (row.allocation_pct.trim() === '') {
        return false;
      }

      const parsed = Number(row.allocation_pct);
      return Number.isNaN(parsed) || parsed < 0;
    });

    return {
      totalPct,
      remainingPct: 100 - totalPct,
      hasInvalidValue,
      isExceeded: totalPct > 100 + 0.0001,
      isComplete: Math.abs(totalPct - 100) < 0.0001,
    };
  }, [editingRows]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedAuxiliaryStage) {
        return;
      }

      const existingAllocations = secondaryAllocationsBySourceStage.get(selectedAuxiliaryStage.id) ?? [];
      const nonZeroRows = editingRows
        .map((row) => ({
          target_stage_id: row.target_stage_id,
          allocation_pct: Number(row.allocation_pct),
        }))
        .filter(
          (row) =>
            !Number.isNaN(row.allocation_pct) &&
            row.allocation_pct > 0,
        );

      for (const allocation of existingAllocations) {
        await deleteIndustrialCostSecondaryAllocation(allocation.id);
      }

      for (const row of nonZeroRows) {
        const payload: CreateSecondaryAllocationPayload = {
          source_stage_id: selectedAuxiliaryStage.id,
          target_stage_id: row.target_stage_id,
          allocation_pct: row.allocation_pct,
          rule_label: null,
          notes: null,
        };

        await createIndustrialCostSecondaryAllocation(payload);
      }
    },
    onSuccess: async () => {
      enqueueSnackbar('Reparto secundario actualizado correctamente.', {
        variant: 'success',
      });
      closeEditor();
      await queryClient.invalidateQueries({
        queryKey: ['industrial-cost-secondary-allocations'],
      });
    },
    onError: (mutationError) => {
      enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' });
    },
  });

  const isLoading =
    stagesQuery.isLoading ||
    movementsQuery.isLoading ||
    classificationsQuery.isLoading ||
    primaryAllocationsQuery.isLoading ||
    secondaryAllocationsQuery.isLoading;

  const isError =
    stagesQuery.isError ||
    movementsQuery.isError ||
    classificationsQuery.isError ||
    primaryAllocationsQuery.isError ||
    secondaryAllocationsQuery.isError;

  const error =
    stagesQuery.error ||
    movementsQuery.error ||
    classificationsQuery.error ||
    primaryAllocationsQuery.error ||
    secondaryAllocationsQuery.error;

  const editorBaseAmount = selectedAuxiliaryStage
    ? stageBaseCostFromPrimary.get(selectedAuxiliaryStage.id) ?? 0
    : 0;

  const editorRedistributedAmount = editorBaseAmount * (editorTotals.totalPct / 100);
  const editorPendingAmount = Math.max(editorBaseAmount - editorRedistributedAmount, 0);

  const editorCanSave =
    !!selectedAuxiliaryStage && !editorTotals.hasInvalidValue && !editorTotals.isExceeded;

  return (
    <>
      <Stack spacing={3}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            backdropFilter: 'blur(10px)',
            px: { xs: 2.5, md: 3 },
            py: { xs: 2.5, md: 3 },
          }}
        >
          <Stack spacing={1.1}>
            <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 900 }}>
              Reparto secundario
            </Typography>

            <Typography variant="body1" sx={{ color: '#cbd5e1', lineHeight: 1.7 }}>
              Aquí redistribuyes el coste acumulado en etapas auxiliares hacia etapas principales.
            </Typography>

            <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.65 }}>
              Cada etapa principal conserva su coste propio acumulado y además recibe el
              coste transferido desde auxiliares. El objetivo es redistribuir sin perder valor.
            </Typography>
          </Stack>
        </Paper>

        {isError ? (
          <Alert severity="error">{getErrorMessage(error)}</Alert>
        ) : isLoading ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid rgba(148,163,184,0.18)',
              bgcolor: 'rgba(15,23,42,0.66)',
              px: 3,
              py: 3,
            }}
          >
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              Cargando reparto secundario...
            </Typography>
          </Paper>
        ) : auxiliaryStages.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid rgba(148,163,184,0.18)',
              bgcolor: 'rgba(15,23,42,0.66)',
              px: 3,
              py: 3,
            }}
          >
            <IndustrialCostSectionPlaceholder
              title="Sin etapas auxiliares activas"
              description="Necesitas al menos una etapa auxiliar activa para abrir el reparto secundario."
            />
          </Paper>
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(3, minmax(0, 1fr))',
                  xl: 'repeat(5, minmax(0, 1fr))',
                },
                gap: 2,
              }}
            >
              <SummaryCard
                label="Etapas auxiliares"
                value={String(summary.auxiliaryCount)}
                helper="Auxiliares activas con coste potencialmente redistribuible."
              />
              <SummaryCard
                label="Coste auxiliar base"
                value={formatAmount(summary.totalAuxiliaryBaseCost)}
                helper="Coste acumulado en auxiliares tras el reparto primario."
              />
              <SummaryCard
                label="Reparto completo"
                value={String(summary.complete)}
                helper="Auxiliares con el 100% ya redistribuido hacia principales."
              />
              <SummaryCard
                label="Pendientes"
                value={String(summary.incomplete)}
                helper="Auxiliares con reparto parcial todavía incompleto."
              />
              <SummaryCard
                label="Sin reparto"
                value={String(summary.noAllocation)}
                helper="Auxiliares que aún no redistribuyen coste a principales."
              />
            </Box>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: '1px solid rgba(148,163,184,0.18)',
                bgcolor: 'rgba(15,23,42,0.66)',
                backdropFilter: 'blur(10px)',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(148,163,184,0.14)' }}>
                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                  Etapas auxiliares y su bolsa de coste
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                  Cada auxiliar muestra su coste base acumulado, cuánto ya ha redistribuido y cuánto queda pendiente.
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 3,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))',
                    xl: 'repeat(3, minmax(0, 1fr))',
                  },
                  gap: 2,
                }}
              >
                {auxiliaryStages.map((stage, index) => {
                  const accent = getAuxiliaryAccent(index);
                  const baseAmount = stageBaseCostFromPrimary.get(stage.id) ?? 0;
                  const redistributed = redistributedAmountByAuxiliaryStage.get(stage.id) ?? 0;
                  const pending = pendingAmountByAuxiliaryStage.get(stage.id) ?? 0;
                  const totalPct = secondaryTotalsBySourceStage.get(stage.id) ?? 0;
                  const status = getCompletionStatus(totalPct);

                  return (
                    <Paper
                      key={stage.id}
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: `1px solid ${accent.borderColor}`,
                        background: accent.softBg,
                        px: 2.25,
                        py: 2.1,
                      }}
                    >
                      <Stack spacing={1.2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                          <Box>
                            <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                              {stage.code}
                            </Typography>
                            <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                              {stage.name}
                            </Typography>
                          </Box>

                          <Chip
                            size="small"
                            label={status.label}
                            sx={{
                              ...getStatusChipStyles(status.tone),
                              fontWeight: 800,
                            }}
                          />
                        </Stack>

                        <Typography
                          variant="h4"
                          sx={{ color: accent.amountColor, fontWeight: 900, letterSpacing: '-0.02em' }}
                        >
                          {formatAmount(baseAmount)}
                        </Typography>

                        <Typography variant="body2" sx={{ color: '#cbd5e1', lineHeight: 1.6 }}>
                          Base acumulada en la auxiliar tras el reparto primario.
                        </Typography>

                        <Stack direction="row" spacing={2}>
                          <Box>
                            <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                              Redistribuido
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                              {formatAmount(redistributed)}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                              Pendiente
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                              {formatAmount(pending)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: '1px solid rgba(148,163,184,0.18)',
                bgcolor: 'rgba(15,23,42,0.66)',
                backdropFilter: 'blur(10px)',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(148,163,184,0.14)' }}>
                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                  Impacto acumulado en etapas principales
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                  Cada principal conserva su coste base y suma lo recibido desde auxiliares.
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 3,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))',
                    xl: 'repeat(3, minmax(0, 1fr))',
                  },
                  gap: 2,
                }}
              >
                {primaryStages.map((stage, index) => {
                  const accent = getPrimaryAccent(index);
                  const ownBase = stageBaseCostFromPrimary.get(stage.id) ?? 0;
                  const received = receivedFromAuxiliariesByPrimaryStage.get(stage.id) ?? 0;
                  const total = finalAccumulatedCostByPrimaryStage.get(stage.id) ?? 0;

                  return (
                    <Paper
                      key={stage.id}
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: `1px solid ${accent.borderColor}`,
                        background: accent.softBg,
                        px: 2.25,
                        py: 2.1,
                      }}
                    >
                      <Stack spacing={1.2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                          <Box>
                            <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                              {stage.code}
                            </Typography>
                            <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                              {stage.name}
                            </Typography>
                          </Box>

                          <Chip
                            size="small"
                            label="Principal"
                            sx={{
                              bgcolor: accent.badgeBg,
                              color: accent.badgeColor,
                              border: `1px solid ${accent.borderColor}`,
                              fontWeight: 800,
                            }}
                          />
                        </Stack>

                        <Typography
                          variant="h4"
                          sx={{ color: accent.amountColor, fontWeight: 900, letterSpacing: '-0.02em' }}
                        >
                          {formatAmount(total)}
                        </Typography>

                        <Stack direction="row" spacing={2}>
                          <Box>
                            <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                              Base propia
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                              {formatAmount(ownBase)}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                              Recibido
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                              {formatAmount(received)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: '1px solid rgba(148,163,184,0.18)',
                bgcolor: 'rgba(15,23,42,0.66)',
                backdropFilter: 'blur(10px)',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(148,163,184,0.14)' }}>
                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                  Mesa de reparto por etapa auxiliar
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                  Cada fila representa una auxiliar. Desde aquí revisas el estado y editas su redistribución hacia principales.
                </Typography>
              </Box>

              <Box
                sx={{
                  px: 3,
                  py: 2.25,
                  borderBottom: '1px solid rgba(148,163,184,0.14)',
                  bgcolor: 'rgba(15,23,42,0.28)',
                }}
              >
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Buscar en toda la tabla"
                    placeholder="Etapa, código, importe, destino, estado"
                    value={tableSearch}
                    onChange={(e) => setTableSearch(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ sx: { color: '#94a3b8' } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#f8fafc',
                        bgcolor: 'rgba(2,6,23,0.38)',
                        '& fieldset': {
                          borderColor: 'rgba(148,163,184,0.22)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(148,163,184,0.34)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'rgba(56,189,248,0.52)',
                        },
                      },
                    }}
                  />

                  <TextField
                    select
                    label="Estado"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as SecondaryStatusFilter)}
                    size="small"
                    sx={{
                      minWidth: { xs: '100%', md: 220 },
                      '& .MuiOutlinedInput-root': {
                        color: '#f8fafc',
                        bgcolor: 'rgba(2,6,23,0.38)',
                        '& fieldset': {
                          borderColor: 'rgba(148,163,184,0.22)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(148,163,184,0.34)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'rgba(56,189,248,0.52)',
                        },
                      },
                    }}
                    InputLabelProps={{ sx: { color: '#94a3b8' } }}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="no-allocation">Sin reparto</MenuItem>
                    <MenuItem value="incomplete">Incompletos</MenuItem>
                    <MenuItem value="complete">Completos</MenuItem>
                    <MenuItem value="exceeded">Excedidos</MenuItem>
                  </TextField>
                </Stack>
              </Box>

              {filteredAuxiliaryStages.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <IndustrialCostSectionPlaceholder
                    title="Sin auxiliares para el filtro actual"
                    description="Ajusta la búsqueda o el filtro de estado para volver a ver auxiliares."
                  />
                </Box>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Auxiliar</TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                          Coste base
                        </TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Principales implicadas</TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                          % completitud
                        </TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                          Redistribuido
                        </TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                          Pendiente
                        </TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Estado</TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                          Acción
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredAuxiliaryStages.map((stage) => {
                        const stageAllocations = secondaryAllocationsBySourceStage.get(stage.id) ?? [];
                        const totalPct = secondaryTotalsBySourceStage.get(stage.id) ?? 0;
                        const redistributed = redistributedAmountByAuxiliaryStage.get(stage.id) ?? 0;
                        const pending = pendingAmountByAuxiliaryStage.get(stage.id) ?? 0;
                        const status = getCompletionStatus(totalPct);

                        const sortedAllocations = stageAllocations
                          .slice()
                          .sort((a, b) => a.target_stage_id.localeCompare(b.target_stage_id));

                        const visibleAllocations = sortedAllocations.slice(0, 2);
                        const hiddenAllocationsCount = Math.max(sortedAllocations.length - 2, 0);

                        return (
                          <TableRow key={stage.id} hover>
                            <TableCell sx={{ minWidth: 260 }}>
                              <Stack spacing={0.4}>
                                <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                                  {stage.name}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                                  {stage.code}
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell align="right" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                              {formatAmount(stageBaseCostFromPrimary.get(stage.id) ?? 0)}
                            </TableCell>

                            <TableCell sx={{ minWidth: 260 }}>
                              {sortedAllocations.length === 0 ? (
                                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                                  Sin principales asignadas todavía
                                </Typography>
                              ) : (
                                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                  {visibleAllocations.map((allocation) => {
                                    const target = stagesMap.get(allocation.target_stage_id);

                                    return (
                                      <Chip
                                        key={allocation.id}
                                        label={`${target?.name || allocation.target_stage_id} · ${formatPct(
                                          allocation.allocation_pct,
                                        )}%`}
                                        size="small"
                                        sx={{
                                          bgcolor: 'rgba(59,130,246,0.14)',
                                          color: '#bfdbfe',
                                          border: '1px solid rgba(59,130,246,0.24)',
                                          fontWeight: 700,
                                        }}
                                      />
                                    );
                                  })}

                                  {hiddenAllocationsCount > 0 ? (
                                    <Chip
                                      label={`+${hiddenAllocationsCount} más`}
                                      size="small"
                                      sx={{
                                        bgcolor: 'rgba(148,163,184,0.12)',
                                        color: '#cbd5e1',
                                        border: '1px solid rgba(148,163,184,0.2)',
                                        fontWeight: 700,
                                      }}
                                    />
                                  ) : null}
                                </Stack>
                              )}
                            </TableCell>

                            <TableCell align="right" sx={{ color: '#f8fafc', fontWeight: 900 }}>
                              {formatPct(totalPct)}%
                            </TableCell>

                            <TableCell align="right" sx={{ color: '#cbd5e1', fontWeight: 800 }}>
                              {formatAmount(redistributed)}
                            </TableCell>

                            <TableCell align="right" sx={{ color: '#cbd5e1', fontWeight: 800 }}>
                              {formatAmount(pending)}
                            </TableCell>

                            <TableCell>
                              <Chip
                                size="small"
                                label={status.label}
                                sx={{
                                  ...getStatusChipStyles(status.tone),
                                  fontWeight: 800,
                                }}
                              />
                            </TableCell>

                            <TableCell align="right">
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => openEditor(stage)}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 800,
                                  borderColor: 'rgba(56,189,248,0.35)',
                                  color: '#bae6fd',
                                  '&:hover': {
                                    borderColor: 'rgba(56,189,248,0.5)',
                                    bgcolor: 'rgba(56,189,248,0.08)',
                                  },
                                }}
                              >
                                Repartir
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Paper>
          </>
        )}
      </Stack>

      <Dialog
        open={!!selectedAuxiliaryStage}
        onClose={() => !saveMutation.isPending && closeEditor()}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            background: 'linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(15,23,42,0.94) 100%)',
            color: '#f8fafc',
            boxShadow: '0 24px 80px rgba(2,6,23,0.55)',
          },
        }}
      >
        <DialogTitle sx={{ color: '#f8fafc', fontWeight: 800 }}>
          Editar reparto secundario
        </DialogTitle>

        <DialogContent>
          {selectedAuxiliaryStage ? (
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.16)',
                  bgcolor: 'rgba(15,23,42,0.72)',
                  px: 2,
                  py: 1.75,
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                      Etapa auxiliar origen
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.25, color: '#f8fafc' }}>
                      {selectedAuxiliaryStage.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 0.65 }}>
                      {selectedAuxiliaryStage.code}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                      Coste base auxiliar
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.25, color: '#f8fafc' }}>
                      {formatAmount(editorBaseAmount)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <Alert severity={editorTotals.isComplete ? 'success' : editorTotals.isExceeded ? 'error' : 'warning'}>
                {editorTotals.isComplete
                  ? 'La auxiliar redistribuye el 100% de su coste. El reparto secundario queda completo.'
                  : editorTotals.isExceeded
                    ? `El reparto supera el 100% en ${formatPct(Math.abs(editorTotals.remainingPct))} puntos. Debes corregirlo antes de guardar.`
                    : `El reparto todavía no llega al 100%. Faltan ${formatPct(
                        Math.max(editorTotals.remainingPct, 0),
                      )} puntos por asignar.`}
              </Alert>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.16)',
                  bgcolor: 'rgba(15,23,42,0.78)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(30,41,59,0.42)',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#f8fafc' }}>
                    Redistribución hacia principales
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 0.25 }}>
                    Introduce el porcentaje que esta auxiliar envía a cada etapa principal.
                  </Typography>
                </Box>

                <Box sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800, color: '#94a3b8' }}>Etapa principal</TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#94a3b8' }} align="right">
                          % asignado
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#94a3b8' }} align="right">
                          Importe recibido
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {editingRows.map((row) => {
                        const pctValue =
                          row.allocation_pct.trim() === '' || Number.isNaN(Number(row.allocation_pct))
                            ? 0
                            : Number(row.allocation_pct);

                        const receivedAmount = editorBaseAmount * (pctValue / 100);

                        return (
                          <TableRow key={row.target_stage_id} hover>
                            <TableCell>
                              <Stack spacing={0.35}>
                                <Typography sx={{ fontWeight: 700, color: '#f8fafc' }}>
                                  {row.target_stage_name}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                                  {stagesMap.get(row.target_stage_id)?.code || row.target_stage_id}
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell align="right" sx={{ width: 180 }}>
                              <TextField
                                value={row.allocation_pct}
                                onChange={(e) => handleRowPctChange(row.target_stage_id, e.target.value)}
                                type="number"
                                size="small"
                                fullWidth
                                inputProps={{ min: 0, step: '0.01' }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    color: '#f8fafc',
                                    bgcolor: 'rgba(2,6,23,0.4)',
                                    '& fieldset': {
                                      borderColor: 'rgba(148,163,184,0.24)',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: 'rgba(148,163,184,0.34)',
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: 'rgba(56,189,248,0.52)',
                                    },
                                  },
                                  '& input': {
                                    textAlign: 'right',
                                  },
                                }}
                              />
                            </TableCell>

                            <TableCell align="right" sx={{ fontWeight: 700, color: '#f8fafc' }}>
                              {formatAmount(receivedAmount)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>

                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    bgcolor: 'rgba(30,41,59,0.42)',
                  }}
                >
                  <Stack direction="row" spacing={3}>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                        Total asignado
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 900, color: '#f8fafc' }}>
                        {formatPct(editorTotals.totalPct)}%
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                        Pendiente
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 900,
                          color: editorTotals.remainingPct < 0 ? '#fca5a5' : '#f8fafc',
                        }}
                      >
                        {formatPct(editorTotals.remainingPct)}%
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                        € redistribuido
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 900, color: '#f8fafc' }}>
                        {formatAmount(editorRedistributedAmount)}
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                        € pendiente
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 900, color: '#f8fafc' }}>
                        {formatAmount(editorPendingAmount)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Paper>

              <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.7 }}>
                Esta edición rehace el reparto de la auxiliar usando las operaciones disponibles en v1
                (eliminación de líneas existentes y creación de nuevas líneas con los porcentajes actuales).
              </Typography>
            </Stack>
          ) : null}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2.5,
            bgcolor: 'rgba(15,23,42,0.92)',
          }}
        >
          <Button
            onClick={closeEditor}
            disabled={saveMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={() => saveMutation.mutate()}
            disabled={!editorCanSave || saveMutation.isPending}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: '#38bdf8',
              color: '#0f172a',
              '&:hover': {
                bgcolor: '#38bdf8',
                filter: 'brightness(1.06)',
              },
            }}
          >
            {saveMutation.isPending ? 'Guardando...' : 'Guardar reparto'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}